// src/layouts/TailwindCdnLayout.tsx
import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";



interface TailwindCdnLayoutProps {
    children: React.ReactNode;
    config?: any;
    scripts?: any;
}

const tailwindGlobalConfig = {
  theme: {
    extend: {
      screens: {
        'hp-sm': '400px',
        'hp-md': '600px',
        'hp-lg': '881px',
        'hp-xl': '1251px',
      },
      colors: {
        // RCNewConfiguration
        primary: "#5A46AA",
        "primary-light": "#A2ADD6",
        accent: "#FF885C",
        "accent-yellow": "#FFDE78",
        "accent-green": "#3d8f7f",
        "light-bg": "#F7F8FC",
        "light-gray": "#E8E9F1",

        // healthTrackerConfig
        "brand-primary": "#5A46AA",
        "brand-primary-light": "#F4F2FF",

        // jobConfigurations
        "brand-purple": "#5A46AA",
        "brand-yellow": "#FFDE78",
        "brand-orange": "#FF885C",
        "brand-light-purple": "#A2ADD6",
        "brand-green": "#53D87F", 
        "brand-gray": "#F3F4F6",
      },

      // If you want ONE shared sans stack, combine them (rather than overriding "sans")
      fontFamily: {
        sans: [
          "Inter",
          "Open Sans",
          "Tahoma",
          "Verdana",
          '"Noto Sans"',
          "sans-serif",
        ],

        // Optional: keep explicit aliases too (handy if different apps want different defaults)
        inter: ["Inter", "sans-serif"],
        opensans: ["Open Sans", "sans-serif"],
        legacy: ['Tahoma', 'Verdana', '"Noto Sans"', "sans-serif"],
      },

      // healthTrackerConfig animations
      keyframes: {
        "fade-in": {
          "0%": { opacity: "0" },
          "100%": { opacity: "1" },
        },
        "fade-in-up": {
          "0%": { opacity: "0", transform: "translateY(20px)" },
          "100%": { opacity: "1", transform: "translateY(0)" },
        },
        "pulse-highlight": {
          "0%, 100%": { "border-color": "rgb(203, 213, 225)", "outline": "2px solid transparent", "outline-offset": "0px" },
          "50%": { "border-color": "rgb(90, 70, 170)", "outline": "2px solid rgba(90, 70, 170, 0.35)", "outline-offset": "1px" },
        },
      },
      animation: {
        "fade-in": "fade-in 0.5s ease-out forwards",
        "fade-in-up": "fade-in-up 0.6s ease-out forwards",
        "pulse-highlight": "pulse-highlight 2s ease-in-out infinite",
      },
    },
  },
};

export default function TailwindCdnLayout({ children, config, scripts }: TailwindCdnLayoutProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const scriptId = "tailwind-cdn-script";

    // If Tailwind script already exists, just configure and mark ready
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existingScript) {
      // If global tailwind already available, apply config and be ready
      const tw = (window as any).tailwind;
      if (tw && typeof tw.config === "function") {
        tw.config(tailwindGlobalConfig);
      } else {
        (window as any).tailwind = (window as any).tailwind || {};
        (window as any).tailwind.config = config ?? tailwindGlobalConfig;
      }
      // Inject direct keyframe CSS (works even if Tailwind CDN hasn't processed custom keyframes yet)
      const styleId = 'pulse-highlight-style';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          @keyframes pulse-highlight {
            0%, 100% { border-color: rgb(203, 213, 225); outline: 2px solid transparent; }
            50% { border-color: rgb(90, 70, 170); outline: 2px solid rgba(90, 70, 170, 0.35); }
          }
          .animate-pulse-highlight {
            animation: pulse-highlight 1.8s ease-in-out infinite !important;
          }
        `;
        document.head.appendChild(style);
      }
      setReady(true);
      return;
    }

    // 1. Inject Tailwind CDN script (same as your index.html)
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://cdn.tailwindcss.com";
    script.async = true;

    // 2. Once Tailwind is loaded, apply config (like your second <script> tag)
    script.onload = () => {
      const tw = (window as any).tailwind;
      if (tw && typeof tw.config === "function") {
        // If CDN exposes config() function, call it
        tw.config(tailwindGlobalConfig);
      } else {
        // Fallback: assign config object
        (window as any).tailwind = (window as any).tailwind || {};
        (window as any).tailwind.config = tailwindGlobalConfig;
      }
      // Inject keyframe CSS directly so it always works on inputs (CDN timing-safe)
      const styleId = 'pulse-highlight-style';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          @keyframes pulse-highlight {
            0%, 100% { border-color: rgb(203, 213, 225); outline: 2px solid transparent; }
            50% { border-color: rgb(90, 70, 170); outline: 2px solid rgba(90, 70, 170, 0.35); }
          }
          .animate-pulse-highlight {
            animation: pulse-highlight 1.8s ease-in-out infinite !important;
          }
        `;
        document.head.appendChild(style);
      }
      setReady(true);
    };

    scripts?.forEach((src: string) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      document.head.appendChild(script);
    });

    document.head.appendChild(script);
  }, [config]);

  if (!ready) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
        <CircularProgress color="primary" />
      </div>
    );
  }

  return <>{children}</>;
}
